import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import './Leagues.css';

/**
 * Persistent sub-navigation for the leagues section: Ligor · Lag · Mina sidor.
 *
 * Makes the team directory a first-class sibling of the league list (instead of
 * a buried header link) and adds a personal "Mina sidor" tab — shown only when
 * logged in — with a badge for pending team invitations. The active tab is
 * derived from the current path.
 *
 * @returns {JSX.Element} The tab bar.
 */
export default function LeaguesTabs() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isLoggedIn, getAuthHeaders } = useAuth();
  const [inviteCount, setInviteCount] = useState(0);

  useEffect(() => {
    if (!isLoggedIn) {
      setInviteCount(0);
      return;
    }
    fetch(`${API_URL}/api/leagues/my-invites`, { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setInviteCount(Array.isArray(d) ? d.length : 0))
      .catch(() => setInviteCount(0));
  }, [isLoggedIn, getAuthHeaders]);

  const active = pathname.startsWith('/leagues/me')
    ? 'me'
    : pathname.startsWith('/leagues/teams')
      ? 'teams'
      : 'leagues';

  return (
    <div className="league-tabs leagues-nav-tabs">
      <button
        className={`league-tab ${active === 'leagues' ? 'is-active' : ''}`}
        onClick={() => navigate('/leagues')}
      >
        {t('nav.leagues')}
      </button>
      <button
        className={`league-tab ${active === 'teams' ? 'is-active' : ''}`}
        onClick={() => navigate('/leagues/teams')}
      >
        {t('createMatch.teams')}
      </button>
      {isLoggedIn && (
        <button
          className={`league-tab ${active === 'me' ? 'is-active' : ''}`}
          onClick={() => navigate('/leagues/me')}
        >
          {t('leagues.tabMine')}
          {inviteCount > 0 && <span className="leagues-tab-badge">{inviteCount}</span>}
        </button>
      )}
    </div>
  );
}
